import styled from '@emotion/styled';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { Remirror as RemirrorTypes } from '@trinity/chat';
import { Lightbulb } from '@trinity/chat-components/components/icons/Lightbulb';
import { MaybeLink } from '@trinity/chat-components/components/presentation/MaybeLink';
import { BasicMarkupEditor } from '@trinity/chat-components/components/Remirror/BasicMarkupEditor';
import type { SetMediaRef } from './interfaces';
import { WidgetQuoteCardMedia } from './Widget.Quote.Card.Media';

type Props = {
  disableMediaControls?: boolean;
  quote: RemirrorTypes.RootNode;
  insights: RemirrorTypes.RootNode;
  color: string;
  speaker: {
    name: string;
    start: number;
    end: number;
  };
  media: {
    type: 'video' | 'audio' | null;
    url: string | null;
  };
  link?: string;
  mediaSetting: 'video' | 'audio' | null;
  setMediaRef: SetMediaRef;
};

export const WidgetQuoteCard = (props: Props) => {
  return (
    <Card color={props.color}>
      <PreviewRoot>
        <QuoteIcon />

        <PreviewWrap>
          <PreviewMain>
            <WidgetQuoteCardMedia
              disableMediaControls={props.disableMediaControls}
              media={props.media}
              mediaSetting={props.mediaSetting}
              speaker={props.speaker}
              setMediaRef={props.setMediaRef}
            />
            <QuoteText>
              <BasicMarkupEditor
                key={JSON.stringify(props.quote)}
                editing={false}
                content={props.quote}
                imageToolbarOptions={{ videos: false }}
              />
            </QuoteText>
            {!isContentEmpty(props.insights) && (
              <InsightsBox>
                <InsightsLabel>
                  <Lightbulb color={props.color} size={12} />
                  <InsightsLabelText color={props.color}>Insights</InsightsLabelText>
                </InsightsLabel>
                <InsightsText>
                  <BasicMarkupEditor
                    key={JSON.stringify(props.insights)}
                    editing={false}
                    content={props.insights}
                    imageToolbarOptions={{ videos: false }}
                  />
                </InsightsText>
              </InsightsBox>
            )}
            <Footer>
              <SpeakerTimestamp>
                <MaybeLink css={{ cursor: props.link ? 'pointer' : 'default' }} to={props.link}>
                  <SpeakerContent>
                    <SpeakerName>{`${props.speaker?.name || 'Speaker'}`}</SpeakerName>
                    {props.speaker?.start != null && (
                      <Timestamp>
                        {`[${secondsToTime(props.speaker.start)} - ${secondsToTime(props.speaker.end)}]`}
                      </Timestamp>
                    )}
                  </SpeakerContent>
                </MaybeLink>
              </SpeakerTimestamp>
            </Footer>
          </PreviewMain>
        </PreviewWrap>
      </PreviewRoot>
    </Card>
  );
};

WidgetQuoteCard.displayName = 'Widget.Quote.Card';

const Card = styled.div<{ color: string }>`
  border: 1px solid var(--sec-01);
  border-radius: 8px;
  background-color: ${(props) => (props.color ? props.color : 'var(--pri-01)')};
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
  min-height: 170px;
  margin-bottom: 15px;
`;

const PreviewRoot = styled.div`
  border-radius: 4px;
  background-color: var(--pri-01);
  width: calc(100% - 10px);
`;

const PreviewWrap = styled.div`
  padding: 5px 20px 10px;
`;

const PreviewMain = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const QuoteText = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  min-height: 90px;
`;

const InsightsBox = styled.div`
  background-color: var(--gray-xxl);
  border: 1px solid var(--sec-01);
  border-radius: 4px;
  margin-bottom: 15px;
`;

const InsightsLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  padding: 2px;
`;

const InsightsLabelText = styled.div`
  font-family: var(--font-semibold);
  color: ${(props: { color: string }) => props.color};
`;

const InsightsText = styled.div`
  font-size: 14px;
  padding: 8px 25px;

  & [contenteditable='false'] {
    font-size: 14px;
    line-height: 18px;
  }
`;

const QuoteIcon = styled(FormatQuoteIcon)`
  color: var(--gray-l);
  font-size: 26px;
`;

const Footer = styled.div``;

const SpeakerTimestamp = styled.button`
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid var(--gray-l);
  padding: 5px 10px;
  cursor: default;
`;

const SpeakerContent = styled.div`
  display: flex;
  gap: 10px;
`;

const SpeakerName = styled.div`
  font-family: var(--font-bold);
`;

const Timestamp = styled.div`
  font-family: var(--font-semibold);
  color: var(--gray-d);
`;

function secondsToTime(value: number) {
  const time = new Date(value * 1000).toISOString().substring(11, 19);
  return time.startsWith('00') ? time.substring(3) : time;
}

function isContentEmpty(value: RemirrorTypes.RootNode) {
  if (!value) return true;

  function isParagraphNodeEmpty(node: RemirrorTypes.ParagraphNode) {
    const content = node.content || [];
    if (!content.length) return true;

    return content.every(isParagraphContentEmpty);
  }

  return (value.content || []).reduce((acc, node) => {
    if (!acc) return acc;

    return isParagraphNodeEmpty(node);
  }, true);
}

function isParagraphContentEmpty(node: RemirrorTypes.TextNode) {
  if (node.type === RemirrorTypes.NodeType.Text) {
    return !node.text.trim().length;
  }

  return false;
}
